import React, { useEffect } from 'react';
import { View, ScrollView, StyleSheet, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from '../../components/common/Text';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import AppToolbar from '../../components/shared/AppToolbar';

const PrivacyPolicyScreen = () => {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isMobile } = useResponsiveLayout();

  // Set meta description and Open Graph tags for web
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute(
        'content',
        'ColourSense Privacy Policy - Learn how we collect, use, and protect your personal information when you use our colour analysis services.'
      );

      // Update Open Graph meta tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', 'ColourSense | Privacy Policy');
      }

      const ogDescription = document.querySelector(
        'meta[property="og:description"]'
      );
      if (ogDescription) {
        ogDescription.setAttribute(
          'content',
          'ColourSense Privacy Policy - Learn how we collect, use, and protect your personal information when you use our colour analysis services.'
        );
      }
    }
  }, []);

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <AppToolbar
        showBackButton={false}
        title="Privacy Policy"
        showProfile={false}
        showSettings={false}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.contentContainer,
          { paddingBottom: insets.bottom + 20 },
        ]}
      >
        <View style={[styles.content, isMobile && styles.contentMobile]}>
          <View style={styles.section}>
            <Text
              style={[
                styles.title,
                { color: colors.text, marginTop: 8, marginBottom: 16 },
              ]}
            >
              ColourSense Privacy Policy
            </Text>
            <Text
              style={[
                styles.lastUpdated,
                {
                  color: colors.secondary,
                  textAlign: 'left',
                  marginBottom: 16,
                },
              ]}
            >
              Last updated: November 25, 2025
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 16 },
              ]}
            >
              At ColourSense (&quot;we,&quot; &quot;our,&quot; or
              &quot;us&quot;), your privacy matters. This Privacy Policy
              explains how you may choose to provide personal information when
              using our website, app, and related services
              (&quot;Services&quot;), how we use it, and how you can control it.
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 16 },
              ]}
            >
              By using ColourSense, you agree to the terms below. If you do not
              agree, please do not use our Services.
            </Text>
          </View>

          <View style={[styles.section, { marginBottom: 8 }]}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              1. Information You May Provide
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              When you use ColourSense, you have control over what information
              you share with us. This includes:
            </Text>
          </View>

          <View style={[styles.section, { marginBottom: 16 }]}>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              a. Account Information
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              If you decide to create an account, you can provide:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Your name{'\n'}• Your email address{'\n'}• A password to secure
              your account
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              This helps us set up and protect your personal profile.
            </Text>
          </View>

          <View style={[styles.section, { marginBottom: 16 }]}>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              b. Profile and User Content
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You may choose to build your profile and upload creative content
              such as:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Profile photo{'\n'}• Saved colour palettes you create or like
              {/* Mood boards with images and captions you share - hidden for now */}
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              This information helps personalize your experience and allows you
              to share your creativity with the community.
            </Text>
          </View>

          <View style={[styles.section, { marginBottom: 16 }]}>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              c. Skin Tone Information (Sensitive Data)
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              To offer you personalized colour palettes, you may voluntarily
              provide your skin tone details—either by selecting options or
              uploading a photo.
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              This information is considered sensitive, so we only use it with
              your explicit consent, solely to generate your color
              recommendations. You can remove or update this information at any
              time.
            </Text>
          </View>

          <View style={[styles.section, { marginBottom: 16 }]}>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              d. Location Information (Optional)
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You may opt to add your city or country to your profile or posts.
              This is completely optional and can be changed or removed whenever
              you want.
            </Text>
          </View>

          <View style={[styles.section, { marginBottom: 16 }]}>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              e. Payment Information
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              ColourSense is currently in Beta and we do not collect payment
              information. When paid plans become available in the future,
              payment will be processed through secure third-party providers and
              we will not store your full payment details on our servers.
            </Text>
          </View>

          <View style={[styles.section, { marginBottom: 16 }]}>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              f. Technical and Usage Information
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              When you visit our Services, we automatically collect some
              information such as:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • IP address{'\n'}• Browser and device details{'\n'}• Usage logs
              and analytics
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              This data helps us keep the service secure and improve its
              performance.
            </Text>
          </View>

          <View style={[styles.section, { marginBottom: 16 }]}>
            <Text style={[styles.subsectionTitle, { color: colors.text }]}>
              g. Cookies and Similar Technologies
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We use cookies and similar tools to:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Keep you logged in{'\n'}• Remember your preferences{'\n'}•
              Analyze usage to improve your experience
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              You can control your cookie preferences via your browser settings.
              When you first visit our site, our cookie banner lets you accept
              or decline non-essential cookies.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              2. How We Use Information You Provide
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We use the information you choose to share to:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Create and personalize your experience{'\n'}• Generate
              customized colour palettes
              {'\n'}• Enable sharing and saving of your creative work{'\n'}•
              Manage your account and subscription status
              {'\n'}• Communicate important updates and offers{'\n'}• Improve
              and maintain our Services{'\n'}• Comply with legal obligations
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              We never sell your personal information to third parties.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              3. Sharing Your Information
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We may share the information you provide with trusted partners who
              support ColourSense, including:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Analytics services (with anonymized data)
              {'\n'}• Email service providers
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              All partners are contractually obligated to protect your data and
              only use it to provide services on our behalf.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              4. Your Privacy Rights
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You have full control over the information you provide:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • You can access, update, or delete your data anytime through your
              account settings.
              {'\n'}• You may withdraw consent for sensitive information like
              skin tone or location data at any time.{'\n'}• Depending on your
              location, you have rights such as data access, correction,
              deletion, portability, and objection to certain data processing.
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              To exercise your rights, contact us at
              security@mail.mycoloursense.com. We may ask for identity
              verification to protect your privacy.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              5. California Privacy Rights
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              If you are a California resident, you have additional rights under
              the CCPA and CPRA, including:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Knowing what personal information we collect and how we use it
              {'\n'}• Requesting deletion of your personal data{'\n'}• Opting
              out of the sale of your personal information (note: we do not sell
              your data){'\n'}• Being free from discrimination for exercising
              your privacy rights
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              For these requests, contact us at security@mail.mycoloursense.com.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              6. Children&apos;s Privacy
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              ColourSense does not knowingly collect information from children
              under 13 (or under 16 in the EU).
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              If you are a parent or guardian and believe a child has provided
              us with information, please contact us so we can promptly delete
              it.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              7. Data Security
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We take reasonable measures to protect the information you share
              with us, including:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Using HTTPS encryption{'\n'}• Storing passwords securely{'\n'}•
              Limiting access to your data{'\n'}• Conducting regular security
              reviews
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              However, no online system is completely secure.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              8. Data Retention
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We keep the information you provide only as long as necessary to
              offer you our Services, comply with legal obligations, or as
              otherwise outlined in this policy.
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              You can delete your account and personal data at any time.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              9. International Data Transfers
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              Your information may be transferred to and stored on servers
              located outside your country of residence. Since data protection
              laws vary across countries, these transfers may involve a
              different level of data protection than what you experience
              locally.
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              To protect your information when it is transferred
              internationally, ColourSense complies with applicable data
              protection laws such as the European Union&apos;s General Data
              Protection Regulation (GDPR) and similar regulations worldwide.
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              When transferring data from the European Economic Area (EEA) or
              other regions with strict data protection laws, we implement
              appropriate safeguards, including:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Standard Contractual Clauses (SCCs): These are legally approved
              contracts between data exporters (us) and data importers (our
              service providers abroad) that require all parties to protect your
              data to EU standards.{'\n'}• Other safeguards: Where applicable,
              we also use additional measures like data encryption and strict
              security protocols to ensure your information remains safe.
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              By using ColourSense, you consent to the transfer, storage, and
              processing of your information outside your country, including in
              countries that may not have data protection laws equivalent to
              those in your jurisdiction.
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              If you have any questions about international data transfers or
              wish to learn more about the safeguards we have in place, please
              contact us at security@mail.mycoloursense.com.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              10. Cookies and Tracking
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              You control cookies and tracking technologies and can adjust your
              preferences or disable them through your browser. When you first
              visit, our cookie banner lets you accept or decline non-essential
              cookies.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              11. Updates to This Policy
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices, legal requirements, or the Services we
              offer. Reasons for updates include:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginLeft: 16 },
              ]}
            >
              • Introducing new features or functionality in ColourSense{'\n'}•
              Complying with changes in applicable data protection laws or
              regulations{'\n'}• Improving the clarity and transparency of how
              we handle your information{'\n'}• Addressing security enhancements
              or vulnerabilities{'\n'}• Responding to feedback or changes in
              industry standards
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              When we make significant changes that affect how we use your
              personal information, we will notify you by email or through a
              notice on our Services before the changes take effect.
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              We encourage you to review this Privacy Policy regularly to stay
              informed about how we protect your information.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              12. Contact Us
            </Text>
            <Text style={[styles.sectionText, { color: colors.secondary }]}>
              For any questions, concerns, or to exercise your data rights,
              please contact ColourSense:
            </Text>
            <Text
              style={[
                styles.sectionText,
                { color: colors.secondary, marginTop: 8 },
              ]}
            >
              Email:{' '}
              <Text style={[styles.sectionText, { color: colors.primary }]}>
                support@mail.mycoloursense.com
              </Text>
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
  },
  content: {
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
  },
  contentMobile: {
    paddingHorizontal: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'left',
  },
  lastUpdated: {
    fontSize: 14,
    marginBottom: 32,
    textAlign: 'center',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 12,
  },
  subsectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    marginTop: 8,
  },
  sectionText: {
    fontSize: 16,
    lineHeight: 24,
  },
});

export default PrivacyPolicyScreen;
